import { format } from 'date-fns'
import { ArrowLeft, Bell, Flag, RefreshCw, Send, ShieldAlert, UserPlus, Wrench } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserLabel } from '@/components/ui/user-label'
import { ADMIN_BASE_URL, adminHref } from '@/config/config'
import { cn } from '@/lib/utils'
import { useInsuranceCarriers, useSetLeadCarriers } from '@/models/insuranceCarrier'
import {
  type ClosedLostDetails,
  closedLostReasonLabels,
  type SalesLead,
  SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
  useAssignLead,
  useCheckCoverwhalePrescreen,
  useCheckGeicoEligibility,
  useConvertLead,
  useFollowLead,
  useGenerateSafetyScore,
  useRefreshFromCab,
  useTransitionLead,
  useUnfollowLead,
} from '@/models/salesLead'
import { useCreateSalesLeadTag, useSalesLeadTags, useSetLeadTags } from '@/models/salesLeadTag'
import { staffUserName, useStaffUsers } from '@/models/user'
import { AddToCampaignDialog } from './AddToCampaignDialog'
import { CabReportButton, isCabReportReady } from './CabReportButton'
import { CarrierSelect } from './CarrierSelect'
import { CheckFmcsaButton } from './CheckFmcsaButton'
import { ClosedLostReasonDialog } from './ClosedLostReasonDialog'
import { FollowUpReminderDialog } from './FollowUpReminderDialog'
import { TagSelect } from './TagSelect'

interface LeadActionSidebarProps {
  lead: SalesLead
  // When set, the lead is shown in a drawer and "Close" dismisses it instead of
  // navigating back to the list/pipeline route.
  onClose?: () => void
}

export function LeadActionSidebar({ lead, onClose }: LeadActionSidebarProps) {
  const navigate = useNavigate()
  // When shown in a drawer `onClose` dismisses it; on the full-page detail route
  // "Close" returns to the leads list.
  const closeHref = '/insure/internal/sales-leads'
  const { staffUsers } = useStaffUsers()
  const assignMutation = useAssignLead()
  const refreshMutation = useRefreshFromCab()
  const generateSafetyScoreMutation = useGenerateSafetyScore()
  const checkGeicoMutation = useCheckGeicoEligibility()
  const checkCoverwhaleMutation = useCheckCoverwhalePrescreen()
  const convertMutation = useConvertLead()
  const transitionMutation = useTransitionLead()
  const [closingAsLost, setClosingAsLost] = useState(false)
  const followMutation = useFollowLead()
  const unfollowMutation = useUnfollowLead()
  const { data: allTags = [] } = useSalesLeadTags()
  const setLeadTagsMutation = useSetLeadTags()
  const createTagMutation = useCreateSalesLeadTag()
  const { carriers } = useInsuranceCarriers()
  const setCarriersMutation = useSetLeadCarriers()
  const prevFollowingRef = useRef(lead.isFollowing)
  const [ringingKey, setRingingKey] = useState(0)
  const [reminderOpen, setReminderOpen] = useState(false)
  const [campaignOpen, setCampaignOpen] = useState(false)
  useEffect(() => {
    if (!prevFollowingRef.current && lead.isFollowing) {
      setRingingKey((k) => k + 1)
    }
    prevFollowingRef.current = lead.isFollowing
  }, [lead.isFollowing])

  const primaryContact = lead.contacts.find((c) => c.isPrimary)
  const primaryEmail = primaryContact?.primaryEmail ?? ''
  const primaryContactName = primaryContact?.name ?? ''
  const canConvert = !lead.convertedOrg && !!primaryEmail && !!primaryContactName.trim()

  function handleConvert() {
    convertMutation.mutate(lead.id, {
      onSuccess: (result) => {
        toast.success('Customer created')
        navigate(`/insure/internal/customers/${result.orgId}`)
      },
      onError: (err: unknown) => {
        const message =
          (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Failed to create customer'
        toast.error(message)
      },
    })
  }

  function handleRefresh() {
    refreshMutation.mutate(lead.id)
  }

  function handleCheckGeico() {
    checkGeicoMutation.mutate(lead.id, {
      onSuccess: (result) => {
        if (result.eligible) {
          toast.success('GEICO eligible')
        } else if (result.existingPolicyholder) {
          toast.warning(result.ineligibilityReason ?? 'Already insured with GEICO — GEICO won’t requote them')
        } else if (result.status === 'busy') {
          toast.warning(result.errorMessage ?? 'GEICO portal is busy, try again shortly')
        } else {
          toast.error(
            result.ineligibilityReason ??
              result.errorMessage ??
              'GEICO check could not be completed — please try again'
          )
        }
      },
      onError: () => toast.error('GEICO eligibility check failed'),
    })
  }

  function handleCheckCoverwhale() {
    checkCoverwhaleMutation.mutate(lead.id, {
      onSuccess: (result) => {
        if (result.status === 'busy') {
          toast.warning(result.errorMessage ?? 'Coverwhale portal is busy, try again shortly')
          return
        }
        if (result.status === 'error') {
          toast.error(
            result.errorMessage ?? 'Coverwhale pre-screen could not be completed — please try again'
          )
          return
        }
        if (result.eligible) {
          toast.success('Coverwhale: eligible for quoting')
          if (result.warnings.length > 0) {
            toast.warning(result.warnings.join('; '))
          }
        } else {
          toast.error(
            result.declineReasons.length > 0
              ? result.declineReasons.join('; ')
              : 'Coverwhale: does not meet eligibility requirements'
          )
        }
      },
      onError: () => toast.error('Coverwhale pre-screen failed'),
    })
  }

  function handleGenerateSafetyScore() {
    generateSafetyScoreMutation.mutate(lead.id, {
      onSuccess: () => toast.success('Safety score generated'),
      onError: () => toast.error('Failed to generate safety score'),
    })
  }

  function handleTransition(targetStatus: SalesLeadStatus) {
    if (targetStatus === lead.status) return
    if (targetStatus === SalesLeadStatus.CLOSED_LOST) {
      setClosingAsLost(true)
      return
    }
    transitionMutation.mutate({ id: lead.id, targetStatus })
  }

  function closeAsLost(details: ClosedLostDetails) {
    transitionMutation.mutate(
      { id: lead.id, targetStatus: SalesLeadStatus.CLOSED_LOST, ...details },
      {
        onSuccess: () => setClosingAsLost(false),
        onError: () => toast.error('Could not move lead — please try again'),
      }
    )
  }

  const tagIds = lead.tags.map((t) => t.id)

  function toggleTag(tagId: string) {
    const next = tagIds.includes(tagId) ? tagIds.filter((id) => id !== tagId) : [...tagIds, tagId]
    setLeadTagsMutation.mutate({ leadId: lead.id, tagIds: next })
  }

  function createAndAssignTag(name: string) {
    createTagMutation.mutate(name, {
      onSuccess: (tag) => {
        if (!tagIds.includes(tag.id)) {
          setLeadTagsMutation.mutate({ leadId: lead.id, tagIds: [...tagIds, tag.id] })
        }
      },
    })
  }

  const assignedCarrierIds = lead.assignedCarriers.map((c) => c.id)

  function toggleCarrier(carrierId: string) {
    const next = assignedCarrierIds.includes(carrierId)
      ? assignedCarrierIds.filter((id) => id !== carrierId)
      : [...assignedCarrierIds, carrierId]
    setCarriersMutation.mutate({ leadId: lead.id, carrierIds: next })
  }

  return (
    <div className="flex w-full flex-col gap-3 lg:w-60 lg:shrink-0">
      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">Assigned to</Label>
        <Select
          value={lead.assignedRep || 'unassigned'}
          onValueChange={(v) => assignMutation.mutate({ id: lead.id, repId: v === 'unassigned' ? null : v })}
          disabled={assignMutation.isPending}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select rep" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {staffUsers.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                <UserLabel name={staffUserName(u)} src={u.avatar} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">Status</Label>
        <Select
          value={lead.status}
          onValueChange={(v) => handleTransition(v as SalesLeadStatus)}
          disabled={transitionMutation.isPending}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(SalesLeadStatus).map((s) => (
              <SelectItem
                key={s}
                value={s}
                disabled={s !== lead.status && !lead.availableTransitions.includes(s)}
              >
                <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', salesLeadStatusColors[s])}>
                  {salesLeadStatusLabels[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {lead.status === SalesLeadStatus.CLOSED_LOST && lead.closedLostReason && (
          <span className="text-muted-foreground text-xs">
            Lost: {closedLostReasonLabels[lead.closedLostReason]}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">Tags</Label>
        <TagSelect
          tags={allTags}
          selectedIds={tagIds}
          onToggle={toggleTag}
          onCreate={createAndAssignTag}
          placeholder="Add tags"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">Carriers</Label>
        <CarrierSelect carriers={carriers} selectedIds={assignedCarrierIds} onToggle={toggleCarrier} />
      </div>

      <div className="my-2 border-t border-neutral-200" />

      {lead.convertedOrg ? (
        <Link to={`/insure/internal/customers/${lead.convertedOrg}`}>
          <Button variant="outline" className="w-full justify-start">
            <UserPlus className="mr-2 size-4" />
            View Customer
          </Button>
        </Link>
      ) : (
        <Button
          onClick={handleConvert}
          disabled={!canConvert || convertMutation.isPending}
          title={canConvert ? undefined : 'Lead must have a contact name and email to create a customer'}
          className="w-full justify-start"
          loading={convertMutation.isPending ? 'Creating...' : undefined}
        >
          <UserPlus className="mr-2 size-4" />
          Create Customer
        </Button>
      )}

      <Button
        variant="outline"
        onClick={() => (lead.isFollowing ? unfollowMutation.mutate(lead.id) : followMutation.mutate(lead.id))}
        disabled={followMutation.isPending || unfollowMutation.isPending}
        className={cn(
          'w-full justify-start',
          lead.isFollowing && 'border-blue-300 bg-blue-100 text-blue-900 hover:bg-blue-200'
        )}
      >
        <Bell
          key={ringingKey}
          className={cn(
            'mr-2 size-4',
            lead.isFollowing && 'fill-current',
            ringingKey > 0 && 'animate-bell-ring'
          )}
        />
        {lead.isFollowing ? 'Following' : 'Follow'}
      </Button>

      <Button
        variant="outline"
        onClick={() => setReminderOpen(true)}
        className={cn(
          'w-full justify-start',
          lead.followUpReminder && 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200'
        )}
      >
        <Flag className={cn('mr-2 size-4', lead.followUpReminder && 'fill-current')} />
        {lead.followUpReminder
          ? `Follow-up: ${format(new Date(lead.followUpReminder.reminderDate), 'MMM d, h:mm a')}`
          : 'Mark follow-up'}
      </Button>
      <FollowUpReminderDialog lead={lead} open={reminderOpen} onOpenChange={setReminderOpen} />
      <ClosedLostReasonDialog
        open={closingAsLost}
        saving={transitionMutation.isPending}
        onCancel={() => setClosingAsLost(false)}
        onConfirm={closeAsLost}
      />

      <Button
        variant="outline"
        onClick={handleRefresh}
        disabled={refreshMutation.isPending}
        className="w-full justify-start"
      >
        <RefreshCw className={cn('mr-2 size-4', refreshMutation.isPending && 'animate-spin')} />
        Refresh from CAB
      </Button>

      <CabReportButton lead={lead} />

      <CheckFmcsaButton lead={lead} />

      {isCabReportReady(lead.cabReport) && !lead.safetyScore && (
        <Button
          variant="outline"
          onClick={handleGenerateSafetyScore}
          disabled={generateSafetyScoreMutation.isPending}
          className="w-full justify-start"
          loading={generateSafetyScoreMutation.isPending ? 'Generating…' : undefined}
        >
          <ShieldAlert
            className={cn('mr-2 size-4', generateSafetyScoreMutation.isPending && 'animate-pulse')}
          />
          Generate safety score
        </Button>
      )}

      <Button
        variant="outline"
        onClick={() => setCampaignOpen(true)}
        disabled={!primaryEmail}
        title={!primaryEmail ? 'Lead must have an email to add to a campaign' : undefined}
        className="w-full justify-start"
      >
        <Send className="mr-2 size-4" />
        Add to Campaign
      </Button>
      <AddToCampaignDialog lead={lead} open={campaignOpen} onOpenChange={setCampaignOpen} />

      <div className="my-2 border-t border-neutral-200" />

      <Button
        variant="outline"
        onClick={handleCheckGeico}
        disabled={checkGeicoMutation.isPending || !lead.dotNumber || !lead.zipCode}
        title={
          !lead.dotNumber
            ? 'Lead must have a DOT number'
            : !lead.zipCode
              ? 'Lead must have a ZIP code'
              : undefined
        }
        className="w-full justify-start"
        loading={checkGeicoMutation.isPending ? 'Checking…' : undefined}
      >
        <img src="https://static.cai.insure/carriers/geico-icon.png" alt="GEICO" className="mr-2 size-4" />
        GEICO eligibility
      </Button>

      <Button
        variant="outline"
        onClick={handleCheckCoverwhale}
        disabled={checkCoverwhaleMutation.isPending || !lead.dotNumber}
        title={!lead.dotNumber ? 'Lead must have a DOT number' : undefined}
        className="w-full justify-start"
        loading={checkCoverwhaleMutation.isPending ? 'Checking…' : undefined}
      >
        <img
          src="https://static.cai.insure/carriers/coverwhale-icon.png"
          alt="Cover Whale"
          className="mr-2 size-4"
        />
        CoverWhale eligibility
      </Button>

      <div className="my-2 border-t border-neutral-200" />

      <Button
        variant="outline"
        href={adminHref(`${ADMIN_BASE_URL}/insure/saleslead/${lead.id}/change/`)}
        target="_blank"
        title="Open this lead in Django admin"
        className="w-full justify-start"
      >
        <Wrench className="mr-2 size-4" />
        Django admin
      </Button>

      {onClose ? (
        <Button variant="link" onClick={onClose} className="w-full justify-start">
          <ArrowLeft className="size-4" />
          Close
        </Button>
      ) : (
        <Button variant="link" href={closeHref} className="w-full justify-start">
          <ArrowLeft className="size-4" />
          Close
        </Button>
      )}
    </div>
  )
}
